import React, { useState, useMemo } from 'react';

/**
 * Roof‑pitch price calculator for carports.
 *
 * HOW IT WORKS
 * 1.  You tell us ONE real price: what a particular width × length costs at 2‑in‑12.
 * 2.  We convert that to a cost‑per‑roof‑square‑foot.
 * 3.  We apply the same $/ft² to any target size and pitch.
 *
 * 👉   Drop the component anywhere in your React app. TailwindCSS is used for styling.
 */

/** Geometric multiplier for any pitch (rise in inches per 12″ run). */
const slopeFactor = (p: number) => Math.sqrt(1 + (p / 12) ** 2);

const PITCHES = [2, 3, 4, 5]; // edit if you need more / fewer options

const PitchCalculator = () => {
	/** Reference price that the builder already knows (2/12 only) */
	const [refWidth, setRefWidth] = useState(24); // ft
	const [refLength, setRefLength] = useState(30); // ft
	const [refPrice, setRefPrice] = useState(1212.5); // $ at 2‑in‑12

	/** Target size you want to price */
	const [tarWidth, setTarWidth] = useState(24);
	const [tarLength, setTarLength] = useState(30);

	/** $ per ft² of roof surface, derived once from the reference size */
	const costPerRoofSqFt = useMemo(() => {
		const areaRef = refWidth * refLength * slopeFactor(2);
		return areaRef ? refPrice / areaRef : 0;
	}, [refWidth, refLength, refPrice]);

	/** Prices for all pitches on the target size */
	const rows = useMemo(() => {
		const planArea = tarWidth * tarLength;
		return PITCHES.map((p) => {
			const price = planArea * slopeFactor(p) * costPerRoofSqFt;
			return { pitch: p, price: price.toFixed(2) };
		});
	}, [tarWidth, tarLength, costPerRoofSqFt]);

	return (
		<div className="mx-auto max-w-lg p-6 bg-white rounded-2xl shadow space-y-6">
			<h2 className="text-2xl font-semibold text-center">
				Roof‑Pitch Price Calculator
			</h2>

			{/* ————————— Reference price inputs ————————— */}
			<fieldset className="border rounded-xl p-4 space-y-3">
				<legend className="px-2 text-sm font-medium">Known 2/12 Price</legend>
				<div className="grid grid-cols-3 gap-4 text-sm">
					<label className="flex flex-col">
						Width (ft)
						<input
							type="number"
							min={1}
							value={refWidth}
							onChange={(e) => setRefWidth(+e.target.value)}
							className="border rounded p-2"
						/>
					</label>
					<label className="flex flex-col">
						Length (ft)
						<input
							type="number"
							min={1}
							value={refLength}
							onChange={(e) => setRefLength(+e.target.value)}
							className="border rounded p-2"
						/>
					</label>
					<label className="flex flex-col col-span-1">
						Price ($)
						<input
							type="number"
							min={0}
							step="0.01"
							value={refPrice}
							onChange={(e) => setRefPrice(+e.target.value)}
							className="border rounded p-2"
						/>
					</label>
				</div>
			</fieldset>

			{/* ————————— Target size inputs ————————— */}
			<fieldset className="border rounded-xl p-4 space-y-3">
				<legend className="px-2 text-sm font-medium">
					Target Size to Quote
				</legend>
				<div className="grid grid-cols-2 gap-4 text-sm">
					<label className="flex flex-col">
						Width (ft)
						<input
							type="number"
							min={1}
							value={tarWidth}
							onChange={(e) => setTarWidth(+e.target.value)}
							className="border rounded p-2"
						/>
					</label>
					<label className="flex flex-col">
						Length (ft)
						<input
							type="number"
							min={1}
							value={tarLength}
							onChange={(e) => setTarLength(+e.target.value)}
							className="border rounded p-2"
						/>
					</label>
				</div>
			</fieldset>

			{/* ————————— Price table ————————— */}
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b">
						<th className="text-left py-1">Pitch</th>
						<th className="text-right py-1">Price ($)</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(({ pitch, price }) => (
						<tr key={pitch} className="border-b last:border-none">
							<td className="py-1">
								{pitch}/12{' '}
								{pitch === 2 && (
									<span className="text-xs text-gray-500">
										(2‑in‑12 reference)
									</span>
								)}
							</td>
							<td className="py-1 text-right">{price}</td>
						</tr>
					))}
				</tbody>
			</table>

			<p className="text-xs text-gray-500 text-center">
				Prices update as you change any value. Calculations assume material &
				labor scale by roof surface area.
			</p>
		</div>
	);
};

export default PitchCalculator;
